import { useArcadeTickets, usePARTBalance } from '@puzzlehq/frontend-utils';
import {
  ConnectResponse,
  PuzzleAccount,
  RecordWithPlaintext,
  useAccount,
  useConnect,
} from '@puzzlehq/sdk';
import { getNestedStringProperty } from '@puzzlehq/types';
import { useSettings } from '@src/data/settings';
import { trpcReact } from '@src/data/trpc';
import { useUserCountryCode } from '@src/hooks/useArcadeStore';
import { TCountryCode } from 'countries-list';
import { createContext, useEffect, useMemo } from 'react';

export type ArcadeStoreContextType = {
  partBalance: number | undefined;
  partBalancePending: number | undefined;
  partBalanceTotal: number | undefined;
  purchasePartBalance: number | undefined;
  partRecords: RecordWithPlaintext[] | undefined;
  descendingPartRecords: RecordWithPlaintext[] | undefined;
  totalCount: number | undefined;
  pendingCount: number | undefined;
  unspentCount: number | undefined;
  account: PuzzleAccount | undefined;
  isPuzzleWalletWebview: boolean | undefined;
  isPro: boolean | undefined;
  countryCode: string | undefined;
  defaultCountryCode: string | undefined;
  isConnected: boolean | undefined;
  connectLoading: boolean | undefined;
  connectError: unknown;
  connect: (() => Promise<ConnectResponse>) | undefined;
};

export const ArcadeStoreContext = createContext<ArcadeStoreContextType | null>(
  null
);

const MAX_PURCHASE_RECORDS = 14;

export const ArcadeStoreProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { account } = useAccount();
  const {
    balancePending,
    balanceTotal,
    records,
    descendingPartRecords,
    totalCount,
    pendingCount,
    unspentCount,
  } = useArcadeTickets();

  const { partBalance } = usePARTBalance();

  const purchasePartBalance = useMemo(() => {
    if (descendingPartRecords === undefined) return undefined;
    const largestRecords = descendingPartRecords.slice(0, MAX_PURCHASE_RECORDS);
    const totalAmount = largestRecords.reduce(
      (sum, record) =>
        sum +
        Number(
          getNestedStringProperty(record.data, ['amount'])?.replace(
            'u64.private',
            ''
          ) ?? 0
        ) /
          1_000_000,
      0
    );

    return totalAmount;
  }, [JSON.stringify(descendingPartRecords)]);

  const { connect, isConnected, loading, error } = useConnect({
    permissions: {
      programIds: {
        [import.meta.env.VITE_NETWORK!]: ['ALL_PROGRAM_IDS'],
      },
    },
    dAppInfo: {
      name: 'Puzzle Arcade Store',
    },
  });

  const { data: isPro } = trpcReact.getIsPro.useQuery(
    { address: account?.address! },
    { enabled: !!account, staleTime: 60_000 }
  );

  const { isPuzzleWalletWebview } = useMemo(() => {
    const userAgent = window.navigator.userAgent;

    if (userAgent.includes('PuzzleWallet')) {
      return { isPuzzleWalletWebview: true };
    }
    return { isPuzzleWalletWebview: false };
  }, [window.navigator.userAgent]);

  const { data: defaultCountryCode } = useUserCountryCode();
  const [storedCountryCode, storeCountryCode] = useSettings((state) => [
    state.countryCode,
    state.setCountryCode,
  ]);

  useEffect(() => {
    if (storedCountryCode === undefined && defaultCountryCode !== undefined) {
      storeCountryCode(defaultCountryCode as TCountryCode);
    }
  }, [storedCountryCode, defaultCountryCode]);

  return (
    <ArcadeStoreContext.Provider
      value={{
        account,
        partBalance: partBalance,
        partBalancePending: balancePending,
        partBalanceTotal: balanceTotal,
        purchasePartBalance,
        partRecords: records,
        descendingPartRecords,
        totalCount,
        pendingCount,
        unspentCount,
        connect,
        connectError: error,
        connectLoading: loading,
        isConnected,
        isPuzzleWalletWebview,
        isPro,
        countryCode: storedCountryCode,
        defaultCountryCode,
      }}
    >
      {children}
    </ArcadeStoreContext.Provider>
  );
};
